import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Train, ArrowLeft, Search, MapPin, Gauge, Timer, Info,
  ChevronRight, Zap, Square, Volume2, VolumeX, Ticket, LocateFixed,
  Bookmark, BookmarkCheck, Trash2, Bell, Navigation, Share2, UtensilsCrossed,
  Copy, Check, Plus, Minus, X, ShoppingCart, Users, Link2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import LocationChip from "@/components/LocationChip";

/**
 * /live-trains — Live tracking for Indian Railways, Vande Bharat, Metro,
 * Namo Bharat / Rapid. Backend is currently a *simulator* — clearly labelled
 * so users understand this is demo-quality until a real feed is wired.
 */
export default function LiveTrains() {
  const [modes, setModes] = useState([]);
  const [mode, setMode] = useState(null);
  const [tab, setTab]   = useState("station");   // 'station' | 'train'
  useSeo({ title: "Live Trains · Metro · Vande Bharat · Namo Bharat · find2near",
           description: "Live train status — arrival time, speed, next station, coach position and seat map. Indian Railways, Vande Bharat, Metro & Namo Bharat / Rapid." });

  useEffect(() => {
    api.get("/trains/modes").then(r => setModes(r.data || []));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950 to-black text-white" data-testid="live-trains-page">
      <div className="relative bg-gradient-to-br from-sky-700 to-indigo-900 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Link to="/apps" className="text-[11px] text-white/70 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={12}/> Back</Link>
          <div className="text-[11px] uppercase tracking-widest font-black opacity-80 flex items-center gap-1 mt-2"><Train size={12}/> Live tracking</div>
          <h1 className="heading text-3xl md:text-5xl font-black mt-1">Live Trains &amp; Metro</h1>
          <p className="text-sm text-white/80 mt-1">Reach-time, speed, next station, coach position &amp; seat map — Railway · Vande Bharat · Metro · Namo Bharat.</p>
          {/* Location freshness chip — Slice 1. Tells the user WHICH
              location the "Nearest station" uses and how fresh it is. */}
          <div className="mt-3" data-testid="live-trains-loc-chip">
            <LocationChip />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Mode chips */}
        <div className="flex flex-wrap gap-1.5" data-testid="train-mode-chips">
          <button onClick={() => setMode(null)}
                  className={`rounded-full text-[11px] font-black px-3 py-1.5 ${mode === null ? "bg-white text-slate-900" : "bg-white/10 text-white"}`}
                  data-testid="train-mode-all">All</button>
          {modes.map(m => (
            <button key={m.mode} onClick={() => setMode(m.mode)}
                    className={`rounded-full text-[11px] font-black px-3 py-1.5 ${mode === m.mode ? "bg-white text-slate-900" : "bg-white/10 text-white"}`}
                    data-testid={`train-mode-${m.mode}`}>
              {m.label} · {m.count}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="inline-flex rounded-full bg-white/5 p-1 text-xs font-black">
          <button onClick={() => setTab("station")}
                  className={`rounded-full px-3 py-1.5 ${tab === "station" ? "bg-white text-slate-900" : "text-white/80"}`}
                  data-testid="train-tab-station">At my station</button>
          <button onClick={() => setTab("train")}
                  className={`rounded-full px-3 py-1.5 ${tab === "train" ? "bg-white text-slate-900" : "text-white/80"}`}
                  data-testid="train-tab-train">Track a train</button>
        </div>

        {tab === "station" ? <StationBoard mode={mode}/> : <TrainTracker mode={mode}/>}

        <div className="text-[10px] text-white/40 flex items-center gap-1" data-testid="train-simulated-note">
          <Info size={11}/> Live data is <span className="font-black text-amber-300">SIMULATED</span> — swap for a real feed by replacing the /trains/track backend adapter.
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
function StationBoard({ mode }) {
  const [q, setQ]   = useState("");
  const [rows, setRows] = useState(null);
  const [nearby, setNearby] = useState([]);
  const [locBusy, setLocBusy] = useState(false);
  const [userLoc, setUserLoc] = useState(null);
  const [dirModal, setDirModal] = useState(null);
  const alertedRef = useRef(new Set());

  // Slice 2: get user location from LocationContext (respects precedence:
  // manual pick > fresh GPS > last known > default). "Nearest" no longer
  // bypasses the app-wide freshness / manual-pin model.
  const {
    loc: ctxLoc, state: locState, age_label: locAge,
    detect: ctxDetect,
  } = useUserLocation() || {};

  const load = async (station) => {
    const s = (station ?? q).trim();
    if (!s) { setRows(null); return; }
    try {
      const r = await api.get("/trains/at-station", {
        params: { station: s, mode: mode || undefined, window_min: 240 },
      });
      const list = r.data || [];
      setRows(list);
      // 5-minute arrival alert — for any train that just crossed the 5-min mark,
      // fire a toast (and browser Notification if permission granted).
      list.forEach(r0 => {
        if (r0.eta_min <= 5 && r0.eta_min > 0 && !alertedRef.current.has(r0.train_no)) {
          alertedRef.current.add(r0.train_no);
          toast.info(`${r0.name} arriving at ${r0.station.name} in ${Math.round(r0.eta_min)} min`);
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Train arriving soon", {
              body: `${r0.name} → ${r0.station.name} · ${Math.round(r0.eta_min)} min`,
              tag: `train-${r0.train_no}`,
            });
          }
        }
      });
    } catch { setRows([]); }
  };
  useEffect(() => { const t = setTimeout(() => load(), 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, mode]);
  // Auto-refresh every 30s while a station is loaded so arrival alerts trigger
  useEffect(() => {
    if (!q.trim()) return;
    const id = setInterval(() => load(), 30000);
    return () => clearInterval(id);
  }, [q, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Resolve which coordinates to use for the "Nearest station" query.
   *
   *  1. If the user picked a location manually (LocationContext.state ==='manual')
   *     → honour it. No GPS re-prompt.
   *  2. If we already have a fresh GPS fix (live/recent) → use it directly.
   *  3. Otherwise (last_known/cached/default) → trigger a fresh detect() so
   *     we don't send a stale reading to /trains/stations/near.
   *
   * Returns { lat, lng, sourceLabel } or null when refusal/unavailable.
   */
  const resolveCoords = async () => {
    if (locState === "manual" && ctxLoc?.lat != null && ctxLoc?.lng != null) {
      return { lat: ctxLoc.lat, lng: ctxLoc.lng, sourceLabel: `manual · ${ctxLoc.city}` };
    }
    if ((locState === "live" || locState === "recent") &&
        ctxLoc?.lat != null && ctxLoc?.lng != null) {
      return { lat: ctxLoc.lat, lng: ctxLoc.lng, sourceLabel: `GPS · ${locAge}` };
    }
    // Ask for a fresh fix. If the user denies we'll fall back to whatever
    // LocationContext already has (even if stale) — better than nothing.
    const fresh = await ctxDetect?.();
    if (fresh?.lat != null && fresh?.lng != null) {
      return { lat: fresh.lat, lng: fresh.lng, sourceLabel: "GPS · just now" };
    }
    if (ctxLoc?.lat != null && ctxLoc?.lng != null && ctxLoc?.source !== "default") {
      return { lat: ctxLoc.lat, lng: ctxLoc.lng,
                sourceLabel: `last known · ${locAge}` };
    }
    return null;
  };

  const detectNearby = async () => {
    setLocBusy(true);
    try {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
      const c = await resolveCoords();
      if (!c) {
        toast.error("Could not get your location. Try tapping the Location chip in the header.");
        return;
      }
      setUserLoc({ lat: c.lat, lng: c.lng });
      const r = await api.get("/trains/stations/near", {
        params: { lat: c.lat, lng: c.lng, limit: 6 },
      });
      setNearby(r.data || []);
      if (r.data?.[0]) {
        setQ(r.data[0].code);
        toast.success(`Closest: ${r.data[0].name} (${r.data[0].distance_km} km · ${c.sourceLabel})`);
      } else {
        toast.info("No stations within 25 km of your location");
      }
    } catch {
      toast.error("Nearby lookup failed");
    } finally {
      setLocBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search station (e.g. NDLS, Rajiv Chowk, Ghaziabad)"
                 className="pl-9 rounded-full bg-white/5 border-white/10 text-white placeholder:text-white/40"
                 data-testid="station-search-input"/>
        </div>
        <Button onClick={detectNearby} disabled={locBusy}
                className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] h-10 px-3"
                data-testid="station-detect-nearby">
          {locBusy ? <Loader2 size={12} className="animate-spin mr-1"/> : <LocateFixed size={12} className="mr-1"/>}
          Nearest
        </Button>
      </div>
      {nearby.length > 0 && (
        <div className="space-y-1" data-testid="nearby-stations">
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {nearby.map(s => (
              <div key={s.code} className={`shrink-0 rounded-2xl px-2 py-1.5 flex items-center gap-1.5 ${q === s.code ? "bg-white text-slate-900" : "bg-white/10 text-white"}`}
                   data-testid={`nearby-station-${s.code}`}>
                <button onClick={() => setQ(s.code)} className="text-[11px] font-black">{s.name} · {s.distance_km}km</button>
                <button onClick={async () => {
                  try {
                    const r = await api.get(`/trains/directions/${s.code}`,
                                            { params: userLoc ? { from_lat: userLoc.lat, from_lng: userLoc.lng } : {} });
                    setDirModal(r.data);
                  } catch { toast.error("Directions unavailable"); }
                }}
                        className={`rounded-full px-1.5 py-0.5 ${q === s.code ? "bg-emerald-500 text-white" : "bg-white/20 text-white hover:bg-white/30"}`}
                        data-testid={`nearby-directions-${s.code}`}>
                  <Navigation size={11}/>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {rows === null ? (
        <div className="text-center text-sm text-white/50 py-6">Type a station or tap <span className="text-emerald-300 font-black">Nearest</span> to auto-fill your closest stop.</div>
      ) : rows.length === 0 ? (
        <div className="text-center text-sm text-white/50 py-6" data-testid="station-empty">No trains arriving in the next 4 hours.</div>
      ) : (
        <div className="space-y-2" data-testid="station-rows">
          {rows.map(r => (
            <div key={r.train_no + r.station.code} className="bg-white/5 rounded-2xl border border-white/10 p-3 flex items-center gap-3"
                 data-testid={`station-row-${r.train_no}`}>
              <div className="text-center min-w-[64px]">
                <div className="text-[10px] uppercase text-white/50 font-black">ETA</div>
                <div className={`text-2xl font-black ${r.eta_min <= 5 ? "text-emerald-300 animate-pulse" : "text-amber-300"}`}>{Math.round(r.eta_min)}</div>
                <div className="text-[9px] text-white/40">min</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase text-sky-300 font-black">{r.mode_label}</div>
                <div className="font-black text-sm truncate">{r.name}</div>
                <div className="text-[11px] text-white/60 truncate">{r.from} → {r.to} · {r.station.code} · {r.station.name}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs font-black text-emerald-400 inline-flex items-center gap-1">
                  <Gauge size={11}/> {r.speed_kmph} km/h
                </div>
                {r.punctuality_min > 3 ? (
                  <div className="text-[10px] text-rose-400 font-black">Late by {Math.round(r.punctuality_min)} min</div>
                ) : (
                  <div className="text-[10px] text-emerald-300 font-black">On time</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {dirModal && <DirectionsModal d={dirModal} onClose={() => setDirModal(null)}/>}
    </div>
  );
}

function DirectionsModal({ d, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="directions-modal">
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-slate-900 border border-white/10 p-5 w-full max-w-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-white text-sm">Get to {d.station.name}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xs">✕</button>
        </div>
        <div className="text-[10px] text-white/50 mt-1">Pick a ride to close the last mile.</div>
        <div className="mt-3 space-y-2">
          {d.providers.map(p => (
            <a key={p.provider} href={p.url} target="_blank" rel="noreferrer"
               className="block bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl px-3 py-2 text-white font-black text-sm"
               data-testid={`directions-provider-${p.provider}`}>
              <span className="mr-2">{p.icon}</span> {p.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
function TrainTracker({ mode }) {
  const { user } = useAuth();
  const [q, setQ]         = useState("");
  const [pnr, setPnr]     = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus]     = useState(null);
  const [coach, setCoach]       = useState("");
  const [seat, setSeat]         = useState("");
  const [coachInfo, setCoachInfo] = useState(null);
  const [pnrDetails, setPnrDetails] = useState(null);
  const [platform, setPlatform]     = useState(null);
  const [savedPnrs, setSavedPnrs]   = useState([]);
  const [saving, setSaving]         = useState(false);
  const [shareModal, setShareModal] = useState(null);
  const [foodModal, setFoodModal]   = useState(null);
  const boardAlertRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/trains/search", { params: { q: q || undefined, mode: mode || undefined, limit: 20 } });
        setResults(r.data || []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, mode]);

  const loadSaved = async () => {
    if (!user) return;
    try { const r = await api.get("/trains/pnrs/mine"); setSavedPnrs(r.data || []); }
    catch { /* silent */ }
  };
  useEffect(() => { loadSaved(); /* eslint-disable-next-line */ }, [user]);

  const refresh = async (trainNo) => {
    try {
      const r = await api.get(`/trains/track/${trainNo}`);
      setStatus({ ...r.data, _loadedAt: Date.now() });
      // If the user is logged in, ping the arrival-notify endpoint. Backend
      // enforces its own 30-min per-station dedup and the 10-min window,
      // so it's safe to fire on every poll — response is a no-op if the
      // train isn't near a station yet or we already alerted for it.
      if (user) {
        try { await api.post(`/trains/${trainNo}/notify-arrival`); }
        catch { /* silent — never let the notify probe break the tracker */ }
      }
    } catch (e) { toast.error("Couldn't load status"); }
  };

  useEffect(() => {
    if (!selected) return;
    refresh(selected);
    const id = setInterval(() => refresh(selected), 12000);
    return () => clearInterval(id);
  }, [selected]);

  const lookupSeat = async () => {
    if (!selected || !coach) return;
    try {
      const r = await api.get(`/trains/coach/${selected}/${coach}`, { params: seat ? { seat } : {} });
      setCoachInfo(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Coach not found"); }
  };

  const lookupPnr = async (rawPnr) => {
    const digits = ((rawPnr ?? pnr) || "").replace(/\D/g, "");
    if (digits.length !== 10) { toast.error("Enter a 10-digit PNR"); return; }
    try {
      const r = await api.get(`/trains/pnr/${digits}`);
      setPnrDetails(r.data);
      setSelected(r.data.train_no);
      setCoach(r.data.coach_no);
      setSeat(r.data.seat);
      setPnr(digits);
      boardAlertRef.current = false;
      toast.success(`PNR ${digits} → ${r.data.train_name}, coach ${r.data.coach_no}, seat ${r.data.seat}`);
      // Fetch platform hint for the FROM station
      try {
        const pr = await api.get(`/trains/platform/${r.data.train_no}/${r.data.from_station.code}`,
                                 { params: { coach: r.data.coach_no } });
        setPlatform(pr.data);
      } catch { setPlatform(null); }
    } catch (e) { toast.error(e?.response?.data?.detail || "PNR lookup failed"); }
  };

  const savePnr = async () => {
    if (!user)     { toast.error("Sign in to save PNRs"); return; }
    if (!pnr)      return;
    setSaving(true);
    try {
      await api.post("/trains/pnrs", { pnr, label: null });
      await loadSaved();
      toast.success("PNR saved to My Journeys");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save PNR"); }
    finally { setSaving(false); }
  };
  const deletePnr = async (p) => {
    try { await api.delete(`/trains/pnrs/${p}`); await loadSaved(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const sharePnr = async (p, hrs = 72) => {
    if (!user) { toast.error("Sign in to share PNRs"); return; }
    try {
      const r = await api.post(`/trains/pnrs/${p}/share`, { pnr: p, expires_hrs: hrs });
      setShareModal(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create share"); }
  };

  // Auto-lookup seat once PNR/status settle
  useEffect(() => {
    if (pnrDetails && selected && coach && status) {
      setTimeout(() => lookupSeat(), 200);
    }
    // eslint-disable-next-line
  }, [pnrDetails, status]);

  // 5-minute boarding alert — when the current status's ETA to the PNR's
  // boarding station drops below 5 min, fire a native Notification once.
  useEffect(() => {
    if (!pnrDetails || !status || boardAlertRef.current) return;
    if (status.next_station?.code !== pnrDetails.from_station.code) return;
    if ((status.eta_next_min || 999) > 5) return;
    boardAlertRef.current = true;
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("Board your train soon", {
        body: `${pnrDetails.train_name} · Platform ${platform?.platform || "—"}, ${platform?.coach?.hint || `Coach ${pnrDetails.coach_no}`}`,
        tag: `pnr-${pnrDetails.pnr}`,
      });
    }
    toast.info(`Board your train soon — Platform ${platform?.platform || "—"} · ${platform?.coach?.walk_zone ? `Zone ${platform.coach.walk_zone}` : `Coach ${pnrDetails.coach_no}`}`);
  }, [status, pnrDetails, platform]);

  const isSaved = savedPnrs.some(x => x.pnr === pnr);

  return (
    <div className="space-y-3">
      {/* PNR — one-tap auto-select */}
      <div className="rounded-3xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-400/20 p-3 flex gap-2" data-testid="pnr-block">
        <div className="relative flex-1">
          <Ticket size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400"/>
          <Input value={pnr} onChange={e => setPnr(e.target.value)} placeholder="Paste your 10-digit PNR to auto-select train + coach + seat"
                 className="pl-9 rounded-full bg-white/5 border-amber-400/30 text-white placeholder:text-white/40" data-testid="pnr-input" maxLength={10}/>
        </div>
        <Button onClick={() => lookupPnr()} className="rounded-full bg-amber-400 hover:bg-amber-500 text-slate-900 font-black text-[11px] h-10 px-3" data-testid="pnr-lookup-btn">Track PNR</Button>
        {pnr && pnrDetails && user && (
          <Button onClick={savePnr} disabled={saving || isSaved}
                  className={`rounded-full font-black text-[11px] h-10 px-3 ${isSaved ? "bg-emerald-500 text-slate-900" : "bg-emerald-500 hover:bg-emerald-600 text-slate-900"}`}
                  data-testid="pnr-save-btn">
            {saving ? <Loader2 size={12} className="animate-spin"/> : isSaved ? <><BookmarkCheck size={12} className="mr-1"/> Saved</> : <><Bookmark size={12} className="mr-1"/> Save</>}
          </Button>
        )}
      </div>

      {/* Saved PNRs — one-tap re-track */}
      {savedPnrs.length > 0 && (
        <div data-testid="saved-pnrs">
          <div className="text-[10px] uppercase font-black text-white/60 mb-1 flex items-center gap-1">
            <BookmarkCheck size={11}/> My saved journeys ({savedPnrs.length})
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {savedPnrs.map(p => (
              <div key={p.pnr} className="shrink-0 bg-white/5 rounded-2xl border border-white/10 p-2 min-w-[220px]" data-testid={`saved-pnr-${p.pnr}`}>
                <div className="text-[10px] font-black text-emerald-300">PNR {p.pnr}</div>
                <div className="font-black text-xs text-white truncate">{p.train_name}</div>
                <div className="text-[10px] text-white/60 truncate">{p.from_station?.name} → {p.to_station?.name}</div>
                <div className="text-[10px] text-white/60">Coach {p.coach_no} · Seat {p.seat} · {p.booking_status}</div>
                <div className="flex gap-1 mt-1">
                  <button onClick={() => lookupPnr(p.pnr)}
                          className="rounded-full bg-amber-400 text-slate-900 text-[10px] font-black px-2 py-0.5 flex-1"
                          data-testid={`saved-pnr-track-${p.pnr}`}>Track</button>
                  <button onClick={() => sharePnr(p.pnr)}
                          className="rounded-full bg-sky-400 text-slate-900 text-[10px] font-black px-2 py-0.5 inline-flex items-center gap-0.5"
                          data-testid={`saved-pnr-share-${p.pnr}`}>
                    <Share2 size={10}/> Share
                  </button>
                  <button onClick={() => deletePnr(p.pnr)}
                          className="rounded-full bg-white/10 text-white/70 hover:text-white px-2 py-0.5"
                          data-testid={`saved-pnr-del-${p.pnr}`}><Trash2 size={11}/></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Train number or name (e.g. 12951, Rajdhani, Vande Bharat)"
               className="pl-9 rounded-full bg-white/5 border-white/10 text-white placeholder:text-white/40"
               data-testid="train-search-input"/>
      </div>
      {!selected && (
        <div className="grid sm:grid-cols-2 gap-2" data-testid="train-search-results">
          {results.map(t => (
            <button key={t.train_no} onClick={() => setSelected(t.train_no)}
                    className="text-left bg-white/5 hover:bg-white/10 rounded-2xl border border-white/10 p-3 transition-colors"
                    data-testid={`train-result-${t.train_no}`}>
              <div className="text-[10px] uppercase text-sky-300 font-black">{t.mode_label}</div>
              <div className="font-black">{t.train_no} · {t.name}</div>
              <div className="text-[11px] text-white/60">{t.from} → {t.to} · {t.stops} stops</div>
            </button>
          ))}
        </div>
      )}

      {selected && status && (
        <div className="space-y-3" data-testid="train-status">
          <button onClick={() => { setSelected(null); setStatus(null); setCoachInfo(null); setPnrDetails(null); setPlatform(null); }}
                  className="text-[11px] text-white/60 hover:text-white inline-flex items-center gap-1"
                  data-testid="train-status-back">
            <ArrowLeft size={11}/> Back to search
          </button>

          {/* Action row — Family Share + Order Food */}
          {(pnrDetails || selected) && (
            <div className="flex flex-wrap gap-2" data-testid="train-action-row">
              <Button
                onClick={() => setFoodModal({ train_no: selected, coach: pnrDetails?.coach_no || coach, seat: pnrDetails?.seat || seat })}
                className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-black text-[11px] h-9 px-3"
                data-testid="order-food-btn">
                <UtensilsCrossed size={12} className="mr-1"/> Order food to seat
              </Button>
              {pnr && pnrDetails && user && (
                <Button onClick={() => sharePnr(pnr)}
                        className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-black text-[11px] h-9 px-3"
                        data-testid="share-journey-btn">
                  <Share2 size={12} className="mr-1"/> Share with family
                </Button>
              )}
            </div>
          )}

          {pnrDetails && (
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-3" data-testid="pnr-summary">
              <div className="text-[10px] uppercase font-black text-emerald-300">PNR {pnrDetails.pnr}</div>
              <div className="text-sm font-black text-emerald-100 mt-1">
                {pnrDetails.from_station.name} → {pnrDetails.to_station.name}
              </div>
              <div className="text-[11px] text-emerald-200 mt-0.5">
                Coach {pnrDetails.coach_no} · Seat {pnrDetails.seat} · Status {pnrDetails.booking_status}
                {pnrDetails.passengers?.length ? ` · ${pnrDetails.passengers.length} passenger(s)` : ""}
              </div>
              {platform && (
                <div className="mt-2 rounded-xl bg-amber-500/10 border border-amber-400/30 p-2" data-testid="platform-hint">
                  <div className="text-[10px] uppercase font-black text-amber-300 flex items-center gap-1"><Bell size={11}/> Boarding platform</div>
                  <div className="text-sm font-black text-amber-100 mt-0.5">
                    Platform {platform.platform} · Halt {platform.halt_min} min
                    {platform.coach?.walk_zone && <> · Walk to <span className="text-amber-300">Zone {platform.coach.walk_zone}</span></>}
                  </div>
                  {platform.coach?.hint && <div className="text-[11px] text-amber-200/80 mt-0.5">{platform.coach.hint}</div>}
                </div>
              )}
            </div>
          )}

          <StatusCard s={status}/>

          <div className="bg-white/5 rounded-3xl border border-white/10 p-3 space-y-3" data-testid="coach-lookup">
            <div className="text-[10px] uppercase font-black text-white/60">Coach position &amp; seat</div>
            <div className="flex gap-2">
              <Input placeholder="Coach (e.g. B2, CC3, M1)" value={coach} onChange={e => setCoach(e.target.value.toUpperCase())}
                     className="rounded-full bg-white/5 border-white/10 text-white flex-1" data-testid="coach-input"/>
              {!/^(ENG|EOG|PC)$/.test(coach) && (
                <Input placeholder="Seat (e.g. 24, 12A)" value={seat} onChange={e => setSeat(e.target.value.toUpperCase())}
                       className="rounded-full bg-white/5 border-white/10 text-white w-32" data-testid="seat-input"/>
              )}
              <Button onClick={lookupSeat} disabled={!coach}
                      className="rounded-full bg-amber-400 text-slate-900 font-black" data-testid="coach-lookup-btn">Find</Button>
            </div>
            {/^(ENG|EOG|PC)$/.test(coach) && (
              <div className="text-[10px] text-amber-300/70">Engine / EOG / Pantry — no seats. Pick a passenger coach like B1, CC1 or M1 to see the seat map.</div>
            )}
            {coachInfo && <CoachCard c={coachInfo}/>}
          </div>

          <CoachStrip status={status}/>
        </div>
      )}

      {shareModal && <ShareLinkModal share={shareModal} onClose={() => setShareModal(null)}/>}
      {foodModal  && <FoodOrderModal ctx={foodModal} onClose={() => setFoodModal(null)}/>}
    </div>
  );
}

function StatusCard({ s }) {
  const { lang } = useI18n();
  const [now, setNow] = useState(Date.now());
  const [voiceOn, setVoiceOn] = useState(false);
  const lastSpokenRef = useRef(null);

  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);

  // Speak an announcement whenever the next-station changes OR ETA crosses 3 min.
  useEffect(() => {
    if (!voiceOn || typeof window === "undefined" || !("speechSynthesis" in window)) return;
    const key = `${s.train_no}|${s.next_station?.code}|${s.status}|${Math.round((s.eta_next_min || 0) / 3)}`;
    if (lastSpokenRef.current === key) return;
    lastSpokenRef.current = key;
    const text = _buildAnnouncement(s, lang);
    if (!text) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = _voiceLangCode(lang);
      u.rate = 0.95; u.pitch = 1.0;
      // Prefer a voice matching the chosen locale for a natural feel
      const voices = window.speechSynthesis.getVoices();
      const match = voices.find(v => v.lang?.toLowerCase().startsWith(u.lang.toLowerCase()));
      if (match) u.voice = match;
      window.speechSynthesis.speak(u);
    } catch { /* silent */ }
  }, [voiceOn, s.next_station?.code, s.status, Math.round((s.eta_next_min || 0) / 3), lang]); // eslint-disable-line

  const etaLive = useMemo(() => {
    if (s.eta_next_min == null) return null;
    const drift = Math.floor((Date.now() - (s._loadedAt || Date.now())) / 60000);
    return Math.max(0, Number(s.eta_next_min) - drift);
  }, [s, now]);

  const progress = s.status === "running" && s.next_station
    ? Math.min(100, Math.max(0, 100 - ((s.distance_to_next_km || 0) / Math.max(1, ((s.next_station.km || 0) - (s.current_station.km || 0))) * 100)))
    : s.status === "at_station" ? 100 : 0;

  return (
    <div className="bg-gradient-to-br from-indigo-800 to-slate-900 rounded-3xl border border-white/10 p-4" data-testid="status-card">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase font-black text-sky-300">{s.mode_label} · {s.train_no}</div>
          <div className="font-black text-lg">{s.name}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setVoiceOn(v => !v)}
                  className={`rounded-full text-[10px] uppercase font-black px-2 py-1 inline-flex items-center gap-1
                              ${voiceOn ? "bg-emerald-500/30 text-emerald-200" : "bg-white/10 text-white/70 hover:text-white"}`}
                  data-testid="voice-announce-toggle">
            {voiceOn ? <><Volume2 size={11}/> Voice on</> : <><VolumeX size={11}/> Voice off</>}
          </button>
          <div className={`rounded-full text-[10px] uppercase font-black px-2 py-1 ${s.status === "running" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}
               data-testid="status-badge">
            {s.status === "running" ? "Running" : "At station"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3">
        <div className="bg-white/5 rounded-2xl p-2 text-center" data-testid="stat-speed">
          <Gauge size={14} className="mx-auto text-emerald-400"/>
          <div className="text-[10px] uppercase text-white/50 font-black mt-1">Speed</div>
          <div className="text-lg font-black">{s.speed_kmph}<span className="text-[10px] text-white/60"> km/h</span></div>
        </div>
        <div className="bg-white/5 rounded-2xl p-2 text-center" data-testid="stat-eta">
          <Timer size={14} className="mx-auto text-amber-400"/>
          <div className="text-[10px] uppercase text-white/50 font-black mt-1">ETA next</div>
          <div className="text-lg font-black">{etaLive != null ? `${Math.round(etaLive)}m` : "—"}</div>
        </div>
        <div className="bg-white/5 rounded-2xl p-2 text-center" data-testid="stat-delay">
          <Zap size={14} className={s.punctuality_min > 3 ? "mx-auto text-rose-400" : "mx-auto text-emerald-400"}/>
          <div className="text-[10px] uppercase text-white/50 font-black mt-1">Delay</div>
          <div className="text-lg font-black">{s.punctuality_min > 3 ? `+${Math.round(s.punctuality_min)}m` : "—"}</div>
        </div>
      </div>

      <div className="mt-3">
        <div className="flex items-center gap-2 text-xs">
          <MapPin size={12} className="text-emerald-400"/>
          <div className="flex-1 font-black" data-testid="current-station">{s.current_station?.name || "—"}</div>
          <ChevronRight size={12} className="text-white/40"/>
          <div className="font-black text-right text-amber-300" data-testid="next-station">{s.next_station?.name || "Terminal"}</div>
        </div>
        <div className="h-2 bg-white/10 rounded-full mt-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-400 to-amber-400 transition-all duration-1000" style={{ width: `${progress}%` }}/>
        </div>
        <div className="text-[10px] text-white/50 mt-1">
          {s.distance_to_next_km != null && `${s.distance_to_next_km.toFixed(1)} km to next stop`}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers for voice announcements — the UI's only i18n island because
// the string catalog on the backend doesn't carry train-specific templates yet.
// ---------------------------------------------------------------------------
const _VOICE_TEMPLATES = {
  en: (s) => s.status === "at_station"
      ? `${s.name} is at ${s.current_station?.name}. Next station: ${s.next_station?.name || "terminal"}.`
      : `Approaching ${s.next_station?.name}. Estimated ${Math.round(s.eta_next_min || 0)} minutes at ${Math.round(s.speed_kmph)} kilometres per hour.`,
  hi: (s) => s.status === "at_station"
      ? `${s.name} ${s.current_station?.name} पर है। अगला स्टेशन: ${s.next_station?.name || "अंतिम"}।`
      : `${s.next_station?.name} की ओर। लगभग ${Math.round(s.eta_next_min || 0)} मिनट, गति ${Math.round(s.speed_kmph)} किलोमीटर प्रति घंटा।`,
  bn: (s) => `পরবর্তী স্টেশন: ${s.next_station?.name || "শেষ"}। প্রায় ${Math.round(s.eta_next_min || 0)} মিনিট।`,
  mr: (s) => `पुढील स्थानक: ${s.next_station?.name || "शेवटचे"}. अंदाजे ${Math.round(s.eta_next_min || 0)} मिनिटे.`,
  ta: (s) => `அடுத்த நிலையம்: ${s.next_station?.name || "இறுதி"}. ${Math.round(s.eta_next_min || 0)} நிமிடங்கள்.`,
  te: (s) => `తదుపరి స్టేషన్: ${s.next_station?.name || "చివరిది"}. ${Math.round(s.eta_next_min || 0)} నిమిషాలు.`,
  gu: (s) => `આગલું સ્ટેશન: ${s.next_station?.name || "અંતિમ"}. લગભગ ${Math.round(s.eta_next_min || 0)} મિનિટ.`,
  kn: (s) => `ಮುಂದಿನ ನಿಲ್ದಾಣ: ${s.next_station?.name || "ಕೊನೆಯದು"}. ಸುಮಾರು ${Math.round(s.eta_next_min || 0)} ನಿಮಿಷಗಳು.`,
  ml: (s) => `അടുത്ത സ്റ്റേഷൻ: ${s.next_station?.name || "അവസാനത്തേത്"}. ഏകദേശം ${Math.round(s.eta_next_min || 0)} മിനിറ്റ്.`,
  pa: (s) => `ਅਗਲਾ ਸਟੇਸ਼ਨ: ${s.next_station?.name || "ਆਖ਼ਰੀ"}. ਲਗਭਗ ${Math.round(s.eta_next_min || 0)} ਮਿੰਟ.`,
  ur: (s) => `اگلا اسٹیشن: ${s.next_station?.name || "آخری"}۔ تقریباً ${Math.round(s.eta_next_min || 0)} منٹ۔`,
  ar: (s) => `المحطة التالية: ${s.next_station?.name || "الأخيرة"}. حوالي ${Math.round(s.eta_next_min || 0)} دقيقة.`,
  fr: (s) => `Prochaine gare : ${s.next_station?.name || "terminus"}. Environ ${Math.round(s.eta_next_min || 0)} minutes.`,
  es: (s) => `Próxima estación: ${s.next_station?.name || "terminal"}. Aproximadamente ${Math.round(s.eta_next_min || 0)} minutos.`,
  de: (s) => `Nächster Halt: ${s.next_station?.name || "Endstation"}. Etwa ${Math.round(s.eta_next_min || 0)} Minuten.`,
  zh: (s) => `下一站:${s.next_station?.name || "终点站"}。大约${Math.round(s.eta_next_min || 0)}分钟。`,
  ja: (s) => `次の駅は${s.next_station?.name || "終点"}です。約${Math.round(s.eta_next_min || 0)}分。`,
  ko: (s) => `다음 역: ${s.next_station?.name || "종점"}. 약 ${Math.round(s.eta_next_min || 0)}분.`,
  ru: (s) => `Следующая станция: ${s.next_station?.name || "конечная"}. Примерно ${Math.round(s.eta_next_min || 0)} минут.`,
  pt: (s) => `Próxima estação: ${s.next_station?.name || "terminal"}. Cerca de ${Math.round(s.eta_next_min || 0)} minutos.`,
  it: (s) => `Prossima stazione: ${s.next_station?.name || "capolinea"}. Circa ${Math.round(s.eta_next_min || 0)} minuti.`,
};

function _buildAnnouncement(s, lang) {
  const tpl = _VOICE_TEMPLATES[lang] || _VOICE_TEMPLATES.en;
  return tpl(s);
}

function _voiceLangCode(l) {
  // Web Speech uses BCP-47 like en-US, hi-IN, ja-JP.
  const map = {
    en: "en-IN", hi: "hi-IN", bn: "bn-IN", mr: "mr-IN", ta: "ta-IN", te: "te-IN",
    gu: "gu-IN", kn: "kn-IN", ml: "ml-IN", pa: "pa-IN", ur: "ur-IN", ar: "ar-SA",
    fr: "fr-FR", es: "es-ES", de: "de-DE", zh: "zh-CN", ja: "ja-JP", ko: "ko-KR",
    ru: "ru-RU", pt: "pt-BR", it: "it-IT",
  };
  return map[l] || `${l}`;
}

function CoachStrip({ status }) {
  const layout = status.coach_layout;
  if (!layout?.coaches?.length) return null;
  return (
    <div className="bg-white/5 rounded-3xl border border-white/10 p-3" data-testid="coach-strip">
      <div className="text-[10px] uppercase font-black text-white/60 mb-2">Consist ({layout.total} coaches)</div>
      <div className="flex gap-1 overflow-x-auto pb-1">
        {layout.coaches.map(c => (
          <div key={c.coach_no}
               className={`shrink-0 min-w-[60px] rounded-xl border p-1.5 text-center ${c.has_seats ? "border-white/20 bg-white/5" : "border-amber-500/30 bg-amber-900/20"}`}
               data-testid={`coach-tile-${c.coach_no}`}>
            <div className="text-[10px] font-black">{c.coach_no}</div>
            <div className="text-[9px] text-white/60 truncate">{c.type}</div>
            {c.has_seats && (
              <>
                <div className="mt-1 h-1.5 bg-white/10 rounded overflow-hidden">
                  <div className={`h-full ${c.occupancy_pct > 80 ? "bg-rose-500" : c.occupancy_pct > 60 ? "bg-amber-500" : "bg-emerald-500"}`}
                       style={{ width: `${c.occupancy_pct}%` }}/>
                </div>
                <div className="text-[9px] text-white/50 mt-0.5">{c.occupancy_pct}%</div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CoachCard({ c }) {
  const perRow = c.per_row || 4;
  const seat = c.seat;
  return (
    <div className="space-y-2" data-testid="coach-card">
      <div className="text-sm">
        <span className="font-black">Coach {c.coach_no}</span>
        <span className="text-white/60"> · {c.type} · position {c.position}/{c.of_total}</span>
      </div>
      <div className="text-[11px] text-white/60">
        {c.adjacent_coaches?.prev && <>← {c.adjacent_coaches.prev} · </>}
        <span className="font-black text-emerald-400">{c.coach_no}</span>
        {c.adjacent_coaches?.next && <> · {c.adjacent_coaches.next} →</>}
      </div>

      {seat ? (
        <div className="bg-white/5 rounded-2xl p-3 space-y-2" data-testid="seat-card">
          <div className="font-black text-amber-300">
            Seat {seat.seat} — row {seat.row}, {seat.side} {seat.is_window ? "window" : seat.is_aisle ? "aisle" : "middle"}
          </div>
          <div className="grid grid-flow-col auto-cols-min gap-1">
            {Array.from({ length: perRow }, (_, i) => i + 1).map(col => {
              const active = seat.col === col;
              return (
                <div key={col}
                     className={`w-8 h-8 rounded flex items-center justify-center text-[10px] font-black
                                 ${active ? "bg-amber-400 text-slate-900" : "bg-white/10 text-white/60"}
                                 ${col === Math.ceil(perRow / 2) ? "mr-2" : ""}`}
                     data-testid={active ? "seat-mine" : undefined}>
                  {active ? seat.number : ""}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        c.total_seats > 0 && (
          <div className="text-[11px] text-white/50 flex items-center gap-1">
            <Square size={10}/> {c.total_seats} seats · {c.per_row} per row · type a seat number above to see your exact spot
          </div>
        )
      )}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// Family Share modal
// ───────────────────────────────────────────────────────────────────────
function ShareLinkModal({ share, onClose }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/trip/${share.share_id}`
    : share.share_url;
  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed"); }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `Track ${share.train_name}`, text: `Follow my train ${share.train_name} live`, url: shareUrl }); }
      catch { /* dismissed */ }
    } else copy();
  };
  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="share-modal">
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-slate-900 border border-white/10 p-5 w-full max-w-sm space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-white text-sm">Share your journey</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xs" data-testid="share-modal-close">✕</button>
        </div>
        <div className="text-[11px] text-white/60">Anyone with this link can watch <span className="text-emerald-300 font-black">{share.train_name}</span> move live. No account needed. Link expires in {Math.round((new Date(share.expires_at) - Date.now()) / 3600000)} hours.</div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-2 text-[11px] text-white/80 break-all font-mono" data-testid="share-url">{shareUrl}</div>
        <div className="flex gap-2">
          <Button onClick={copy} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] flex-1" data-testid="share-copy-btn">
            {copied ? <><Check size={12} className="mr-1"/> Copied</> : <><Copy size={12} className="mr-1"/> Copy link</>}
          </Button>
          <Button onClick={nativeShare} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-black text-[11px] flex-1" data-testid="share-native-btn">
            <Share2 size={12} className="mr-1"/> Share
          </Button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// In-Train Food Order modal
// ───────────────────────────────────────────────────────────────────────
export function FoodOrderModal({ ctx, onClose, onPlaced }) {
  const [tab, setTab] = useState("solo");         // solo | basket
  const [menu, setMenu] = useState(null);
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [basket, setBasket] = useState(null);     // {basket_code, share_url, ...}
  const [basketBusy, setBasketBusy] = useState(false);

  useEffect(() => {
    if (!ctx?.train_no) return;
    api.get(`/trains/food/menu`, { params: { train_no: ctx.train_no } })
       .then(r => setMenu(r.data))
       .catch(() => toast.error("Menu unavailable"));
  }, [ctx?.train_no]);

  const total = useMemo(() => {
    if (!menu) return 0;
    return menu.menu.reduce((s, m) => s + (cart[m.id] || 0) * m.price, 0);
  }, [menu, cart]);

  const bump = (id, delta) => {
    setCart(c => {
      const next = Math.max(0, (c[id] || 0) + delta);
      const clone = { ...c }; if (next === 0) delete clone[id]; else clone[id] = next; return clone;
    });
  };

  const place = async () => {
    const items = Object.entries(cart).filter(([, q]) => q > 0).map(([id, qty]) => ({ id, qty }));
    if (!items.length) { toast.error("Add at least one item"); return; }
    setLoading(true);
    try {
      const r = await api.post("/trains/food/order", {
        train_no: ctx.train_no,
        coach_no: ctx.coach || undefined,
        seat: ctx.seat || undefined,
        items,
        payment_mode: "cod",
      });
      setPlaced(r.data);
      toast.success(`Order placed — delivery at ${r.data.station.name}`);
      onPlaced?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Order failed"); }
    finally { setLoading(false); }
  };

  const startBasket = async () => {
    setBasketBusy(true);
    try {
      const r = await api.post("/trains/food/basket/new", {
        train_no: ctx.train_no,
        label: "Family feast",
      });
      const url = `${window.location.origin}/basket/${r.data.basket_code}`;
      setBasket({ ...r.data, share_url: url });
      try { await navigator.clipboard.writeText(url); toast.success(`Basket ${r.data.basket_code} — link copied`); }
      catch { toast.info(`Share code: ${r.data.basket_code}`); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Basket create failed"); }
    finally { setBasketBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-end sm:place-items-center p-2 sm:p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="food-modal">
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-slate-900 border border-white/10 p-4 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-black text-white text-sm inline-flex items-center gap-1"><UtensilsCrossed size={13}/> Order to your seat</h3>
            {menu && <div className="text-[10px] text-white/60">Delivered at <span className="text-emerald-300 font-black">{menu.station.name}</span>{menu.eta_min != null && <> · ETA {Math.round(menu.eta_min)} min</>}</div>}
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xs" data-testid="food-modal-close"><X size={14}/></button>
        </div>

        {!placed && !basket && (
          <div className="inline-flex rounded-full bg-white/5 p-1 text-[11px] font-black" data-testid="food-tabs">
            <button onClick={() => setTab("solo")}   className={`rounded-full px-3 py-1 ${tab === "solo" ? "bg-white text-slate-900" : "text-white/70"}`} data-testid="food-tab-solo">Just for me</button>
            <button onClick={() => setTab("basket")} className={`rounded-full px-3 py-1 inline-flex items-center gap-1 ${tab === "basket" ? "bg-white text-slate-900" : "text-white/70"}`} data-testid="food-tab-basket"><Users size={11}/> Family basket</button>
          </div>
        )}

        {placed ? (
          <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-3 space-y-1" data-testid="food-order-placed">
            <div className="text-[10px] uppercase font-black text-emerald-300">Order confirmed</div>
            <div className="text-sm font-black text-emerald-100">{placed.order_id}</div>
            <div className="text-[11px] text-emerald-200">Delivery at {placed.station.name} · ETA {Math.round(placed.eta_delivery_min)} min · Coach {placed.coach_no || "—"} Seat {placed.seat || "—"}</div>
            <div className="text-[11px] text-emerald-200">Total ₹{placed.total} · Pay on delivery</div>
            <Button onClick={onClose} className="rounded-full bg-emerald-500 text-slate-900 font-black text-[11px] mt-1">Done</Button>
          </div>
        ) : basket ? (
          <div className="rounded-2xl bg-sky-500/10 border border-sky-400/30 p-3 space-y-2" data-testid="food-basket-created">
            <div className="text-[10px] uppercase font-black text-sky-300 inline-flex items-center gap-1"><Users size={11}/> Family basket ready</div>
            <div className="text-xl font-black text-sky-100 tracking-widest" data-testid="food-basket-code">{basket.basket_code}</div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-2 text-[11px] text-white/80 break-all font-mono" data-testid="food-basket-url">{basket.share_url}</div>
            <div className="text-[11px] text-sky-200">Share the code or link with your family. Everyone adds their own items with their coach + seat. All parcels deliver together at <span className="font-black">{basket.station?.name}</span>.</div>
            <div className="flex gap-2">
              <Button onClick={async () => {
                try { await navigator.clipboard.writeText(basket.share_url); toast.success("Link copied"); } catch { /* silent */ }
              }} className="rounded-full bg-emerald-500 text-slate-900 font-black text-[11px] flex-1" data-testid="food-basket-copy-btn"><Copy size={11} className="mr-1"/> Copy link</Button>
              <Link to={`/basket/${basket.basket_code}`} onClick={onClose}
                    className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-black text-[11px] px-4 h-9 inline-flex items-center justify-center" data-testid="food-basket-open-btn"><Link2 size={11} className="mr-1"/> Open basket</Link>
            </div>
          </div>
        ) : !menu ? (
          <div className="grid place-items-center py-8"><Loader2 className="animate-spin"/></div>
        ) : tab === "basket" ? (
          <div className="space-y-2" data-testid="food-basket-intro">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-1">
              <div className="text-[11px] text-white font-black inline-flex items-center gap-1"><Users size={12} className="text-sky-400"/> Pool one order across coaches</div>
              <div className="text-[11px] text-white/60">Kids in Coach B while you're in A? Create a Family Basket — each seat picks their own items, everyone eats together at the next stop.</div>
              <ul className="text-[11px] text-white/70 list-disc pl-4 space-y-0.5">
                <li>You get a 6-char code + link to share</li>
                <li>Family members add items with their coach + seat</li>
                <li>You close the basket → one COD order to the vendor</li>
              </ul>
            </div>
            <Button disabled={basketBusy} onClick={startBasket}
                    className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-black text-[11px] w-full h-10" data-testid="food-basket-create-btn">
              {basketBusy ? <Loader2 size={12} className="animate-spin"/> : <><Users size={12} className="mr-1"/> Start Family Basket</>}
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-1.5" data-testid="food-menu">
              {menu.menu.map(m => (
                <div key={m.id} className="flex items-center gap-2 bg-white/5 rounded-2xl border border-white/10 p-2" data-testid={`food-item-${m.id}`}>
                  <div className={`w-3 h-3 rounded-sm border ${m.veg ? "border-emerald-400" : "border-rose-400"} flex items-center justify-center`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${m.veg ? "bg-emerald-400" : "bg-rose-400"}`}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm text-white truncate">{m.name}</div>
                    <div className="text-[10px] text-white/50 truncate">{m.vendor} · ₹{m.price}</div>
                  </div>
                  {cart[m.id] ? (
                    <div className="inline-flex items-center gap-1 bg-emerald-500 rounded-full text-slate-900 font-black text-xs px-1">
                      <button onClick={() => bump(m.id, -1)} data-testid={`food-item-minus-${m.id}`}><Minus size={12}/></button>
                      <span className="min-w-[16px] text-center" data-testid={`food-item-qty-${m.id}`}>{cart[m.id]}</span>
                      <button onClick={() => bump(m.id, +1)} data-testid={`food-item-plus-${m.id}`}><Plus size={12}/></button>
                    </div>
                  ) : (
                    <Button onClick={() => bump(m.id, +1)} className="rounded-full bg-emerald-500 text-slate-900 font-black text-[10px] h-7 px-2" data-testid={`food-item-add-${m.id}`}>
                      <Plus size={11} className="mr-0.5"/> Add
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <div className="text-[10px] text-white/50 border-t border-white/10 pt-2">{menu.cutoff_note}</div>
            <div className="flex items-center justify-between">
              <div className="font-black text-white text-sm" data-testid="food-cart-total">
                <ShoppingCart size={12} className="inline mr-1"/> Total ₹{total}
              </div>
              <Button disabled={!total || loading} onClick={place} className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-black text-[11px] h-9 px-4" data-testid="food-place-btn">
                {loading ? <Loader2 size={12} className="animate-spin"/> : `Place order · COD`}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
